import {ResultColumn} from "./reportApi";

/**
 * A result, as a CSV file.
 *
 * BUILT IN THE BROWSER, FROM ROWS THAT ARE ALREADY THERE. The obvious alternative — a server
 * endpoint returning text/csv — would be a second place that serves tenant rows, needing its own
 * guard, its own persona handling and its own realm scoping, to produce a file out of data the
 * page is already holding. Nothing is disclosed here that was not already on screen, which is what
 * makes this an ordinary download rather than an export.
 *
 * THE VALUES ARE TENANT-AUTHORED, and that is the whole reason this file is careful. A report
 * column is very often a FORM ANSWER — text somebody typed into a form — so every escaping rule
 * below is defending against content, not against formatting.
 */

/**
 * Spreadsheet formula injection: the one CSV rule that is a security control rather than a syntax
 * one.
 *
 * A cell whose text begins `=`, `+`, `-`, `@`, tab or carriage return is interpreted by Excel,
 * Sheets and LibreOffice as a FORMULA when the file is opened. `=HYPERLINK(...)` exfiltrates,
 * `=cmd|'/c calc'!A1` has historically executed. The content comes from form answers, so a tenant
 * can author exactly that string and the person who opens the export is the victim.
 *
 * A LEADING APOSTROPHE IS THE FIX, and it is the one every spreadsheet understands: it forces the
 * cell to text and is not shown. Quoting alone does NOT help — a quoted field is still parsed as a
 * formula once the quotes come off.
 */
const FORMULA_LEAD = /^[=+\-@\t\r]/;

export function csvCell(value: unknown): string {
    if (value === null || value === undefined) return "";
    let text = String(value);

    if (FORMULA_LEAD.test(text)) text = `'${text}`;

    // QUOTED WHEN IT HAS TO BE, and not otherwise: a file where every field is quoted is harder to
    // read and no more correct. A quote inside a quoted field is doubled — that is the rule, and
    // backslashes are not an escape in CSV however much they look like one.
    if (/[",\r\n]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
    return text;
}

export function resultToCsv(columns: ResultColumn[], rows: unknown[][]): string {
    const lines = [
        (columns ?? []).map(one => csvCell(one.Label)).join(","),
        ...(rows ?? []).map(row => (row ?? []).map(csvCell).join(",")),
    ];
    // CRLF, which is what RFC 4180 says and what Excel on Windows expects. Everything else copes.
    return lines.join("\r\n");
}

/**
 * What the saved file is called.
 *
 * NAMED FOR THE REPORT AND THE MOMENT, because a folder of `report.csv` files tells nobody which
 * report or when — and a scheduled report produces one of these every day.
 */
export function csvFilename(reportName: string | null | undefined, when?: string | null): string {
    const name = (reportName || "report").replace(/[^\w\-. ]+/g, "").trim() || "report";
    const stamp = String(when || new Date().toISOString()).slice(0, 19).replace(/[:T]/g, "-");
    return `${name} ${stamp}.csv`;
}

/**
 * Hand the file to the browser.
 *
 * A BOM, because Excel opens a UTF-8 CSV as the local codepage without one and turns every accented
 * name into mojibake. Three bytes that nothing else notices.
 *
 * THE OBJECT URL IS REVOKED. Without it the blob is held for the life of the page, and somebody
 * exporting repeatedly from a dashboard-length session leaks every one of them.
 */
export function downloadCsv(filename: string, csv: string): void {
    const blob = new Blob(["﻿", csv], {type: "text/csv;charset=utf-8"});
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
}
